/**
 * Dialog criar/editar de automação — sempre cross-mesa: o select de mesa fica
 * no próprio form ('Todas as mesas' = automação global). POST/PUT em
 * /automations com deskId no corpo; na edição a mesa PODE mudar (o servidor
 * valida e reposiciona).
 */
import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import type {
  Automation,
  AutomationAction,
  AutomationConditions,
  AutomationCreateBody,
  AutomationTrigger,
  AutomationUpdateBody,
  DeskDetail,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, Skeleton } from '@/components/ui/misc';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  clientsLookupOptions,
  deskDetailOptions,
  desksListOptions,
  errorMessage,
} from '@/features/desks/desks-queries';
import { teamListQuery } from '@/features/team/team-queries';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { emailTemplatesOptions } from '@/features/templates/templates-queries';

export const TRIGGER_LABELS: Record<AutomationTrigger, string> = {
  ticket_created: 'Ticket criado',
  stage_entered: 'Entrou no estágio',
  stage_exited: 'Saiu do estágio',
  comment_added: 'Comentário adicionado',
  ticket_closed: 'Ticket fechado',
  stagnant_in_stage: 'Estagnado no estágio',
};

export const ACTION_LABELS: Record<AutomationAction['type'], string> = {
  send_email: 'Enviar e-mail',
  set_stage: 'Alterar estágio',
  set_status: 'Alterar status',
  set_priority: 'Alterar prioridade',
  set_responsible: 'Alterar responsável',
  add_comment: 'Adicionar comentário',
};

/** Corpo completo do PUT a partir da automação (toggle de ativo na lista). */
export function automationBodyFrom(a: Automation, active: boolean): AutomationUpdateBody {
  return {
    deskId: a.deskId,
    name: a.name,
    trigger: a.trigger,
    conditions: a.conditions,
    actions: a.actions,
    active,
  };
}

// ── Rascunho de ações ───────────────────────────────────────────────────────

type ActionDraft =
  | {
      key: number;
      type: 'send_email';
      templateId: string;
      toRequestor: boolean;
      toResponsible: boolean;
      toCustom: boolean;
      customEmails: string;
    }
  | { key: number; type: 'set_stage'; stageId: string }
  | { key: number; type: 'set_status'; statusId: string }
  | { key: number; type: 'set_priority'; priorityId: string }
  | { key: number; type: 'set_responsible'; userId: string }
  | { key: number; type: 'add_comment'; body: string; internal: boolean };

let draftKey = 0;
const nextKey = () => ++draftKey;

function emptyDraft(type: AutomationAction['type']): ActionDraft {
  const key = nextKey();
  switch (type) {
    case 'send_email':
      return {
        key,
        type,
        templateId: '',
        toRequestor: true,
        toResponsible: false,
        toCustom: false,
        customEmails: '',
      };
    case 'set_stage':
      return { key, type, stageId: '' };
    case 'set_status':
      return { key, type, statusId: '' };
    case 'set_priority':
      return { key, type, priorityId: '' };
    case 'set_responsible':
      return { key, type, userId: '' };
    case 'add_comment':
      return { key, type, body: '', internal: true };
  }
}

function toDraft(action: AutomationAction): ActionDraft {
  const key = nextKey();
  switch (action.type) {
    case 'send_email':
      return {
        key,
        type: 'send_email',
        templateId: String(action.templateId),
        toRequestor: action.to.includes('requestor'),
        toResponsible: action.to.includes('responsible'),
        toCustom: action.to.includes('custom'),
        customEmails: (action.customEmails ?? []).join(', '),
      };
    case 'set_stage':
      return { key, type: 'set_stage', stageId: String(action.stageId) };
    case 'set_status':
      return { key, type: 'set_status', statusId: String(action.statusId) };
    case 'set_priority':
      return { key, type: 'set_priority', priorityId: String(action.priorityId) };
    case 'set_responsible':
      return { key, type: 'set_responsible', userId: action.userId === null ? 'none' : String(action.userId) };
    case 'add_comment':
      return { key, type: 'add_comment', body: action.body, internal: action.internal };
  }
}

/** Converte o rascunho em ação do contrato; string = erro de validação. */
function fromDraft(draft: ActionDraft): AutomationAction | string {
  const label = ACTION_LABELS[draft.type];
  switch (draft.type) {
    case 'send_email': {
      if (!draft.templateId) return `${label}: selecione o modelo.`;
      const to: ('requestor' | 'responsible' | 'custom')[] = [];
      if (draft.toRequestor) to.push('requestor');
      if (draft.toResponsible) to.push('responsible');
      if (draft.toCustom) to.push('custom');
      if (to.length === 0) return `${label}: selecione ao menos um destinatário.`;
      const customEmails = draft.customEmails
        .split(/[,;\s]+/)
        .map((e) => e.trim())
        .filter(Boolean);
      if (draft.toCustom && customEmails.length === 0) {
        return `${label}: informe os e-mails personalizados.`;
      }
      return {
        type: 'send_email',
        templateId: Number(draft.templateId),
        to,
        ...(draft.toCustom ? { customEmails } : {}),
      };
    }
    case 'set_stage':
      if (!draft.stageId) return `${label}: selecione o estágio.`;
      return { type: 'set_stage', stageId: Number(draft.stageId) };
    case 'set_status':
      if (!draft.statusId) return `${label}: selecione o status.`;
      return { type: 'set_status', statusId: Number(draft.statusId) };
    case 'set_priority':
      if (!draft.priorityId) return `${label}: selecione a prioridade.`;
      return { type: 'set_priority', priorityId: Number(draft.priorityId) };
    case 'set_responsible':
      if (!draft.userId) return `${label}: selecione o responsável.`;
      return {
        type: 'set_responsible',
        userId: draft.userId === 'none' ? null : Number(draft.userId),
      };
    case 'add_comment':
      if (!draft.body.trim()) return `${label}: escreva o comentário.`;
      return { type: 'add_comment', body: draft.body.trim(), internal: draft.internal };
  }
}

/** Ações permitidas em automação global ("Todas as mesas") — contrato da API. */
const GLOBAL_ACTION_TYPES: readonly AutomationAction['type'][] = ['send_email', 'add_comment'];

/** Forma mínima que o dialog precisa — o item de GET /automations serve. */
export interface AutomationLike {
  id: number;
  /** null = automação global ('Todas' as mesas) */
  deskId: number | null;
  name: string;
  active: boolean;
  trigger: AutomationTrigger;
  conditions: AutomationConditions;
  actions: AutomationAction[];
}

export function AutomationDialog({
  automation,
  onClose,
}: {
  automation: AutomationLike | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const clients = useQuery(clientsLookupOptions());
  // só a equipe deste tenant (pedido da PoolTrack, 05/10) — a API recusa
  // responsável de fora dela; inativos entram só se já escolhidos (ver abaixo)
  const team = useQuery(teamListQuery({ includeInactive: true }));
  const templates = useQuery(emailTemplatesOptions());

  // ── Mesa: 'Todas as mesas' = automação global; muda também na edição ──────
  const [deskId, setDeskId] = useState<number | null>(automation?.deskId ?? null);
  const desksList = useQuery(desksListOptions());
  const deskDetail = useQuery({
    ...deskDetailOptions(deskId ?? 0),
    enabled: deskId !== null,
  });
  const effectiveDesk: DeskDetail | null = deskId !== null ? (deskDetail.data ?? null) : null;
  const isGlobal = deskId === null;
  // Configuração da mesa selecionada ainda em voo (ou com erro): sem ela os
  // pickers de estágio/prioridade/status fingiriam mesa sem configuração.
  const deskLoading = deskId !== null && deskDetail.isPending;
  const deskFailed = deskId !== null && deskDetail.isError;

  // Scrub no mount: row global legado pode carregar condições/ações de mesa
  // (conditions.stageId/priorityIds, gatilho de estagnação, ações de estágio…)
  // — sem limpar, o submit 422a sem campo visível para corrigir.
  const mountGlobal = automation !== null && automation.deskId === null;
  const [name, setName] = useState(automation?.name ?? '');
  const [trigger, setTrigger] = useState<AutomationTrigger>(() => {
    const t = automation?.trigger ?? 'ticket_created';
    return mountGlobal && t === 'stagnant_in_stage' ? 'ticket_created' : t;
  });
  const [stageId, setStageId] = useState(() =>
    !mountGlobal && automation?.conditions.stageId != null
      ? String(automation.conditions.stageId)
      : 'any'
  );
  const [priorityIds, setPriorityIds] = useState<number[]>(() =>
    mountGlobal ? [] : (automation?.conditions.priorityIds ?? [])
  );
  const [clientIds, setClientIds] = useState<number[]>(automation?.conditions.clientIds ?? []);
  const [stagnantMinutes, setStagnantMinutes] = useState(() =>
    !mountGlobal && automation?.conditions.stagnantMinutes != null
      ? String(automation.conditions.stagnantMinutes)
      : ''
  );
  const [actions, setActions] = useState<ActionDraft[]>(() => {
    const drafts = automation?.actions.map(toDraft) ?? [];
    return mountGlobal ? drafts.filter((a) => GLOBAL_ACTION_TYPES.includes(a.type)) : drafts;
  });
  const [validationError, setValidationError] = useState<string | null>(null);
  const actionsEndRef = useRef<HTMLDivElement | null>(null);

  const save = useMutation({
    mutationFn: async (body: AutomationCreateBody) => {
      // deskId no corpo: número = mesa, null = global — o PUT muda mesa/global
      // com validação e reposicionamento no servidor.
      const res = automation
        ? await api<Automation>(`/automations/${automation.id}`, { method: 'PUT', body })
        : await api<Automation>('/automations', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      // prefixo largo: cobre a lista cross-mesa e caches por mesa
      void queryClient.invalidateQueries({ queryKey: ['erp', 'automations'] });
      onClose();
    },
  });

  const stages = [...(effectiveDesk?.stages ?? [])].sort((a, b) => a.position - b.position);
  const priorities = [...(effectiveDesk?.priorities ?? [])].sort((a, b) => a.position - b.position);

  const toggleId = (list: number[], id: number): number[] =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  /** Troca de mesa: condições e ações presas à mesa anterior deixam de valer. */
  const changeDesk = (value: string) => {
    const next = value === 'all' ? null : Number(value);
    if (next === deskId) return;
    setDeskId(next);
    setStageId('any');
    setPriorityIds([]);
    setValidationError(null);
    if (next === null) {
      // global: sem gatilho de estagnação e só ações de e-mail/comentário
      if (trigger === 'stagnant_in_stage') setTrigger('ticket_created');
      setStagnantMinutes('');
      setActions((prev) => prev.filter((a) => GLOBAL_ACTION_TYPES.includes(a.type)));
    } else {
      setActions((prev) =>
        prev.map((a) => {
          if (a.type === 'set_stage') return { ...a, stageId: '' };
          if (a.type === 'set_status') return { ...a, statusId: '' };
          if (a.type === 'set_priority') return { ...a, priorityId: '' };
          return a;
        })
      );
    }
  };

  const addAction = (type: AutomationAction['type']) => {
    setActions((prev) => [...prev, emptyDraft(type)]);
    // rola o builder até a ação recém-adicionada
    setTimeout(() => actionsEndRef.current?.scrollIntoView({ block: 'nearest' }), 0);
  };

  const patchAction = (key: number, patch: Partial<ActionDraft>) => {
    setActions((prev) => prev.map((a) => (a.key === key ? ({ ...a, ...patch } as ActionDraft) : a)));
  };

  const removeAction = (key: number) => {
    setActions((prev) => prev.filter((a) => a.key !== key));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || deskLoading || deskFailed) return;

    if (trigger === 'stagnant_in_stage') {
      const minutes = Number(stagnantMinutes);
      if (!Number.isInteger(minutes) || minutes <= 0) {
        setValidationError('Informe os minutos de estagnação (inteiro positivo).');
        return;
      }
      if (stageId === 'any') {
        setValidationError('Selecione o estágio observado para o gatilho de estagnação.');
        return;
      }
    }
    if (actions.length === 0) {
      setValidationError('Adicione ao menos uma ação.');
      return;
    }
    const built: AutomationAction[] = [];
    for (const draft of actions) {
      const result = fromDraft(draft);
      if (typeof result === 'string') {
        setValidationError(result);
        return;
      }
      built.push(result);
    }
    setValidationError(null);

    save.mutate({
      deskId,
      name: name.trim(),
      trigger,
      conditions: {
        ...(stageId !== 'any' ? { stageId: Number(stageId) } : {}),
        ...(priorityIds.length > 0 ? { priorityIds } : {}),
        ...(clientIds.length > 0 ? { clientIds } : {}),
        ...(trigger === 'stagnant_in_stage' ? { stagnantMinutes: Number(stagnantMinutes) } : {}),
      },
      actions: built,
      active: automation?.active ?? true,
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={automation ? 'Editar automação' : 'Nova automação'}
      className="sm:max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>
            Mesas <span className="text-destructive">*</span>
          </Label>
          <Select value={deskId === null ? 'all' : String(deskId)} onValueChange={changeDesk}>
            <SelectTrigger size="sm" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as mesas</SelectItem>
              {[...(desksList.data?.items ?? [])]
                .sort((a, b) => a.position - b.position)
                .map((d) => (
                  <SelectItem key={d.id} value={String(d.id)}>
                    {d.displayName}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {isGlobal ? (
            <p className="text-xs text-muted-foreground">
              Automação para todas as mesas: sem condições de estágio/prioridade, sem gatilho
              de estagnação e ações limitadas a &quot;Enviar e-mail&quot; e &quot;Adicionar
              comentário&quot;.
            </p>
          ) : null}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="automation-name">
              Nome <span className="text-destructive">*</span>
            </Label>
            <Input
              id="automation-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label>
              Gatilho <span className="text-destructive">*</span>
            </Label>
            <Select value={trigger} onValueChange={(v) => setTrigger(v as AutomationTrigger)}>
              <SelectTrigger size="sm" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(TRIGGER_LABELS) as AutomationTrigger[])
                  .filter((t) => !isGlobal || t !== 'stagnant_in_stage')
                  .map((t) => (
                    <SelectItem key={t} value={t}>
                      {TRIGGER_LABELS[t]}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {deskLoading ? (
          // Configuração da mesa em voo: sem pickers vazios fingindo mesa sem
          // estágios/prioridades — skeleton e salvar desabilitado.
          <div className="space-y-3" aria-busy>
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : deskFailed ? (
          <p className="rounded-md border border-divider p-3 text-sm text-destructive">
            Erro ao carregar a configuração da mesa: {errorMessage(deskDetail.error)}
          </p>
        ) : (
          <>
            {/* ── Condições ────────────────────────────────────────────────── */}
            <fieldset className="space-y-3 rounded-md border border-divider p-3">
              <legend className="px-1 text-sm font-semibold">Condições</legend>
              {isGlobal ? null : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>
                      Estágio
                      {trigger === 'stagnant_in_stage' ? (
                        <span className="text-destructive"> *</span>
                      ) : null}
                    </Label>
                    <Select value={stageId} onValueChange={setStageId}>
                      <SelectTrigger size="sm" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Qualquer estágio</SelectItem>
                        {stages.map((s) => (
                          <SelectItem key={s.id} value={String(s.id)}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {trigger === 'stagnant_in_stage' ? (
                    <div className="space-y-1.5">
                      <Label htmlFor="automation-stagnant">
                        Minutos estagnado <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="automation-stagnant"
                        type="number"
                        min={1}
                        value={stagnantMinutes}
                        onChange={(e) => setStagnantMinutes(e.target.value)}
                        placeholder="Ex.: 120"
                      />
                    </div>
                  ) : null}
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {isGlobal ? null : (
                  <div className="space-y-1.5">
                    <Label>Prioridades</Label>
                    {priorities.length === 0 ? (
                      <p className="text-[13px] text-muted-foreground">Mesa sem prioridades.</p>
                    ) : (
                      <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border border-divider p-2">
                        {priorities.map((p) => (
                          <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm">
                            <Checkbox
                              checked={priorityIds.includes(p.id)}
                              onCheckedChange={() => setPriorityIds((prev) => toggleId(prev, p.id))}
                            />
                            <span className="truncate">{p.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">Nenhuma marcada = todas.</p>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Clientes</Label>
                  {clients.isPending ? (
                    <Skeleton className="h-24 w-full" />
                  ) : (clients.data ?? []).length === 0 ? (
                    <p className="text-[13px] text-muted-foreground">Nenhum cliente cadastrado.</p>
                  ) : (
                    <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border border-divider p-2">
                      {(clients.data ?? []).map((c) => (
                        <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
                          <Checkbox
                            checked={clientIds.includes(c.id)}
                            onCheckedChange={() => setClientIds((prev) => toggleId(prev, c.id))}
                          />
                          <span className="truncate">{c.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Nenhum marcado = todos.</p>
                </div>
              </div>
            </fieldset>

            {/* ── Ações ────────────────────────────────────────────────────── */}
            <fieldset className="space-y-2 rounded-md border border-divider p-3">
              <legend className="px-1 text-sm font-semibold">Ações</legend>
              {actions.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">
                  Nenhuma ação — use “Adicionar ação” abaixo.
                </p>
              ) : (
                <ul className="space-y-2">
                  {actions.map((draft, index) => (
                    <li key={draft.key} className="rounded-md border border-divider p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">
                          {index + 1}. {ACTION_LABELS[draft.type]}
                        </span>
                        <DeskRowAction
                          label="Remover ação"
                          destructive
                          onClick={() => removeAction(draft.key)}
                        >
                          <Trash2 className="size-3.5" />
                        </DeskRowAction>
                      </div>
                      <ActionFields
                        draft={draft}
                        desk={effectiveDesk}
                        templates={templates.data ?? []}
                        users={(team.data?.items ?? []).filter(
                          (m) =>
                            m.active ||
                            (draft.type === 'set_responsible' && draft.userId === String(m.id))
                        )}
                        onPatch={(patch) => patchAction(draft.key, patch)}
                      />
                    </li>
                  ))}
                </ul>
              )}
              <div ref={actionsEndRef} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="sm">
                    <Plus className="size-4" strokeWidth={2.5} />
                    Adicionar ação
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {(Object.keys(ACTION_LABELS) as AutomationAction['type'][])
                    .filter((type) => !isGlobal || GLOBAL_ACTION_TYPES.includes(type))
                    .map((type) => (
                      <DropdownMenuItem key={type} onSelect={() => addAction(type)}>
                        {ACTION_LABELS[type]}
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </fieldset>
          </>
        )}

        {validationError ? <p className="text-sm text-destructive">{validationError}</p> : null}
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={save.isPending || !name.trim() || deskLoading || deskFailed}
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ActionFields({
  draft,
  desk,
  templates,
  users,
  onPatch,
}: {
  draft: ActionDraft;
  desk: DeskDetail | null;
  templates: { id: number; name: string; active: boolean }[];
  users: { id: number; name: string }[];
  onPatch: (patch: Partial<ActionDraft>) => void;
}) {
  switch (draft.type) {
    case 'send_email':
      return (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>
              Modelo de e-mail <span className="text-destructive">*</span>
            </Label>
            <Select value={draft.templateId} onValueChange={(v) => onPatch({ templateId: v })}>
              <SelectTrigger size="sm" className="w-full">
                <SelectValue placeholder="Selecione o modelo" />
              </SelectTrigger>
              <SelectContent>
                {templates.length === 0 ? (
                  <SelectItem value="0" disabled>
                    Nenhum modelo cadastrado
                  </SelectItem>
                ) : (
                  templates.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                      {!t.active ? ' (inativo)' : ''}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>
              Destinatários <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.toRequestor}
                  onCheckedChange={(c) => onPatch({ toRequestor: c === true })}
                />
                Solicitante
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.toResponsible}
                  onCheckedChange={(c) => onPatch({ toResponsible: c === true })}
                />
                Responsável
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.toCustom}
                  onCheckedChange={(c) => onPatch({ toCustom: c === true })}
                />
                Personalizado
              </label>
            </div>
          </div>
          {draft.toCustom ? (
            <div className="space-y-1.5">
              <Label>E-mails personalizados</Label>
              <Input
                value={draft.customEmails}
                onChange={(e) => onPatch({ customEmails: e.target.value })}
                placeholder="um@exemplo.com, outro@exemplo.com"
              />
            </div>
          ) : null}
        </div>
      );
    case 'set_stage':
      return (
        <Select value={draft.stageId} onValueChange={(v) => onPatch({ stageId: v })}>
          <SelectTrigger size="sm" className="w-full">
            <SelectValue placeholder="Selecione o estágio" />
          </SelectTrigger>
          <SelectContent>
            {[...(desk?.stages ?? [])]
              .sort((a, b) => a.position - b.position)
              .map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      );
    case 'set_status':
      return (
        <Select value={draft.statusId} onValueChange={(v) => onPatch({ statusId: v })}>
          <SelectTrigger size="sm" className="w-full">
            <SelectValue placeholder="Selecione o status" />
          </SelectTrigger>
          <SelectContent>
            {(desk?.statuses ?? []).map((s) => (
              <SelectItem key={s.id} value={String(s.id)}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'set_priority':
      return (
        <Select value={draft.priorityId} onValueChange={(v) => onPatch({ priorityId: v })}>
          <SelectTrigger size="sm" className="w-full">
            <SelectValue placeholder="Selecione a prioridade" />
          </SelectTrigger>
          <SelectContent>
            {[...(desk?.priorities ?? [])]
              .sort((a, b) => a.position - b.position)
              .map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      );
    case 'set_responsible':
      return (
        <Select value={draft.userId} onValueChange={(v) => onPatch({ userId: v })}>
          <SelectTrigger size="sm" className="w-full">
            <SelectValue placeholder="Selecione o responsável" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Remover responsável</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.id} value={String(u.id)}>
                {u.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'add_comment':
      return (
        <div className="space-y-2">
          <Textarea
            value={draft.body}
            onChange={(e) => onPatch({ body: e.target.value })}
            placeholder="Texto do comentário"
            rows={3}
          />
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={draft.internal}
              onCheckedChange={(c) => onPatch({ internal: c === true })}
            />
            Comentário interno
          </label>
        </div>
      );
  }
}
